'use client';

import { Button, cn, Sheet, SheetContent, SheetDescription, SheetTitle } from '@shared/components/shadcn';
import { ChevronLeft } from '@shared/icons';
import { useIsMobile } from '@shared/utils';
import type * as React from 'react';
import { useEffect, useRef } from 'react';

type FeedbackSheetProps = {
  children: React.ReactNode;
  title: string;
  dataTestIdBaseName: string;
  className?: string;
  contentClassName?: string;
  showBackButton?: boolean;
  showCloseButton?: boolean;
  onBack?: () => void;
  footer?: React.ReactNode;
  desktopSide?: 'left' | 'right';
  mobileHeight?: string;
  desktopWidth?: string;
  'data-track-feature'?: string;
  'data-track-container'?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

function FeedbackSheet({
  children,
  title,
  className,
  contentClassName,
  showBackButton = false,
  showCloseButton = true,
  onBack,
  footer,
  desktopSide = 'right',
  desktopWidth = 'max-w-md',
  mobileHeight = 'h-[100dvh]',
  'data-track-feature': dataTrackFeature,
  'data-track-container': dataTrackContainer,
  dataTestIdBaseName,
  open = true,
  onOpenChange,
  ...props
}: FeedbackSheetProps) {
  const isMobile = useIsMobile();
  const contentRef = useRef<HTMLDivElement>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      previousActiveElementRef.current = (document.activeElement as HTMLElement) || null;

      if (contentRef.current) {
        const firstInteractive = contentRef.current.querySelector<HTMLElement>(
          'button:not([disabled]), [role="button"]:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );

        if (firstInteractive) {
          setTimeout(() => {
            firstInteractive.focus({ preventScroll: true });
          }, 100);
        } else {
          const titleElement = contentRef.current.querySelector<HTMLElement>('[data-testid="feedback-sheet-header-title"]');
          if (titleElement) {
            titleElement.setAttribute('tabIndex', '-1');
            setTimeout(() => {
              titleElement.focus({ preventScroll: true });
            }, 100);
          }
        }
      }
    }
  }, [open]);

  const handleCloseAutoFocus = (e: Event) => {
    if (previousActiveElementRef.current) {
      e.preventDefault();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const element = previousActiveElementRef.current;
          if (element) {
            element.focus({ preventScroll: true });
          }
        });
      });
    }
  };

  const getResponsiveClasses = () => {
    if (isMobile) {
      return cn('w-full max-w-none mx-0 p-0 dark:bg-background', contentClassName, mobileHeight);
    }
    return cn('w-full mx-auto p-0 h-[100vh] rounded-none dark:bg-background', desktopWidth, contentClassName);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} {...props}>
      <SheetContent
        data-testid={`${dataTestIdBaseName}-sheet`}
        side={isMobile ? 'bottom' : desktopSide}
        className={getResponsiveClasses()}
        closeIconSize="extra-small"
        showCloseButton={showCloseButton}
        closeTrackName="Close"
        onCloseAutoFocus={handleCloseAutoFocus}
        {...(dataTrackFeature && { 'data-track-feature': dataTrackFeature })}
        {...(dataTrackContainer && { 'data-track-container': dataTrackContainer })}
        dataTestIdBaseName={dataTestIdBaseName}
      >
        <div ref={contentRef} className={cn('flex flex-col h-full', className)} data-testid="feedback-sheet-content">
          <div className="flex items-center justify-between p-4 border-b border-border shrink-0 min-h-[60px]">
            {showBackButton ? (
              <div className="flex items-center">
                <Button data-testid="feedback-back-button" aria-label="Go back" variant="secondary" className="rounded-full p-2 h-8 w-8" onClick={onBack} data-track-name="Back">
                  <ChevronLeft size="large" />
                </Button>
              </div>
            ) : (
              <div className="h-8 w-8" />
            )}
            <div className="flex-1 flex items-center justify-center">
              <SheetTitle className="paragraph-explanatory-strong" data-testid="feedback-sheet-header-title">
                {title}
              </SheetTitle>
              <SheetDescription className="sr-only" data-testid="feedback-sheet-description">
                {title}
              </SheetDescription>
            </div>
            <div className="h-8 w-8" />
          </div>

          <div className="flex flex-col flex-1 min-h-0">
            {children}
            {footer && <div className="py-10 md:pb-0 mt-auto md:mt-0">{footer}</div>}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export { FeedbackSheet };
